/**
 * Difficulty tiers picked on the start screen. Systems read the active profile
 * through `difficulty()` at the few places a value is consumed (spawn HP, damage
 * to the wizard, spawner cadence, boss tuning, heals, score), so Config stays the
 * single source of base numbers and nothing is mutated at runtime.
 *
 * Density is the hard lever, not HP. A smaller pack that peels, lunges and
 * stomps should feel dangerous; a 40-body blob just feels unfair. liveCap is
 * the ceiling — do not "fix" empty rooms by raising burst sizes.
 */
export type DifficultyId = 'apprentice' | 'adept' | 'archmage';

export interface DifficultyProfile {
  id: DifficultyId;
  name: string;
  /** Vaelen's one-liner for the menu card. */
  blurb: string;
  /** Short stat readout under the blurb. */
  stats: string;
  /** Small fry, golems, gargoyles and portals. */
  enemyHp: number;
  bossHp: number;
  /** Every hit the wizard takes: contact, gargoyle bolts, boss spells, dragon fire. */
  damageTaken: number;
  /** Gargoyle bolts, boss bolts and dragon flames. */
  projectileSpeed: number;
  /** Portal pump cadence. */
  spawnRate: number;
  /** Extra small fry per portal burst (can be negative). */
  burstBonus: number;
  /** Hard ceiling on one portal cough. Packs stay readable. */
  burstMax: number;
  /**
   * Soft cap on concurrent small fry (imp / skull / slime / bat share PoolKind.Imp).
   * Portals skip a burst when the room is already at cap and refill as kills free slots.
   * DEV: this is the density ceiling. Do not raise burstMax or burstBonus to fill a room.
   */
  liveCap: number;
  /** Added to RoomLayout.spawnerCountForTier (can be negative). */
  portalBonus: number;
  /** Hard max portals a room will plant, after the bonus. */
  portalMax: number;
  /** Scale on wallEnemyCount. Apprentice and early rooms stay sparse. */
  gargoyleScale: number;
  /** Boss idle gap between patterns. */
  bossRest: number;
  /** Seconds before Cinderwyrm is released. */
  stallSeconds: number;
  /** Rune heart and room-clear heal amounts. */
  heal: number;
  /** Heart drop odds off small fry, slimes and golems. */
  heartDrop: number;
  /** Extra lives after death this run. Infinity is unlimited. The next death after 0 is final. */
  continues: number;
  scoreMult: number;
}

/** HUD / menu readout. Unlimited continues render as infinity, not "Infinity". */
export function formatContinues(n: number): string {
  return Number.isFinite(n) ? String(n) : '∞';
}

export const DIFFICULTIES: readonly DifficultyProfile[] = [
  {
    id: 'apprentice',
    name: 'Apprentice',
    blurb: 'A thin pack that still thinks. The imps peel, the hearts drop fast, and nobody watching has to know.',
    stats: 'PACK CAP 10  |  DAMAGE TAKEN 70%  |  CONT ∞  |  SCORE x0.75',
    enemyHp: 0.9,
    bossHp: 0.75,
    damageTaken: 0.7,
    projectileSpeed: 0.85,
    spawnRate: 0.52,
    burstBonus: 0,
    burstMax: 2,
    liveCap: 10,
    portalBonus: 0,
    portalMax: 2,
    gargoyleScale: 0.4,
    bossRest: 1.4,
    stallSeconds: 1.5,
    heal: 1.4,
    heartDrop: 1.6,
    continues: Infinity,
    scoreMult: 0.75,
  },
  {
    id: 'adept',
    name: 'Adept',
    blurb: 'The show as it airs: a readable pack, real stakes, and a mop on standby.',
    stats: 'PACK CAP 17  |  DAMAGE TAKEN 100%  |  CONT 2  |  SCORE x1',
    enemyHp: 1,
    bossHp: 1,
    damageTaken: 1,
    projectileSpeed: 1,
    spawnRate: 0.88,
    burstBonus: 0,
    burstMax: 3,
    liveCap: 17,
    portalBonus: 0,
    portalMax: 5,
    gargoyleScale: 0.75,
    bossRest: 1,
    stallSeconds: 1,
    heal: 1,
    heartDrop: 1,
    continues: 2,
    scoreMult: 1,
  },
  {
    id: 'archmage',
    name: 'Archmage',
    blurb: 'Fewer bodies than the old riot, but they hunt. Hits land harder. The sponsors already love you. Briefly.',
    stats: 'PACK CAP 24  |  DAMAGE TAKEN 125%  |  CONT 1  |  SCORE x1.5',
    enemyHp: 1.15,
    bossHp: 1.2,
    damageTaken: 1.25,
    projectileSpeed: 1.12,
    spawnRate: 1.05,
    burstBonus: 0,
    burstMax: 3,
    liveCap: 24,
    portalBonus: 0,
    portalMax: 6,
    gargoyleScale: 1,
    bossRest: 0.82,
    stallSeconds: 0.8,
    heal: 0.75,
    heartDrop: 0.65,
    continues: 1,
    scoreMult: 1.5,
  },
];

const STORAGE_KEY = 'wa.difficulty';
const DEFAULT_INDEX = 1;

let current: DifficultyProfile = DIFFICULTIES[DEFAULT_INDEX];

export function difficulty(): DifficultyProfile {
  return current;
}

export function difficultyIndex(): number {
  return DIFFICULTIES.indexOf(current);
}

export function setDifficulty(id: DifficultyId): DifficultyProfile {
  const next = DIFFICULTIES.find((d) => d.id === id);
  if (next) current = next;
  return current;
}

/** Remembered choice from the last visit, or Adept. */
export function loadDifficulty(): DifficultyProfile {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) setDifficulty(saved as DifficultyId);
  } catch {
    // Storage blocked: default tier.
  }
  return current;
}

export function saveDifficulty(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, current.id);
  } catch {
    // Private mode: the choice just does not persist.
  }
}

/** Score helper so every award rounds the same way. */
export function scaleScore(points: number): number {
  return Math.round(points * current.scoreMult);
}

/** Room portal count after the start-screen tier budget. Always at least one. */
export function portalBudget(rawCount: number): number {
  const d = current;
  return Math.max(1, Math.min(d.portalMax, rawCount + d.portalBonus));
}

/** Wall-gun count after the start-screen tier budget. */
export function wallGunBudget(rawCount: number): number {
  if (rawCount <= 0) return 0;
  return Math.max(0, Math.round(rawCount * current.gargoyleScale));
}
